import { apiClient } from '@/lib/api-client';
import type { ApiSuccess } from '@/lib/api-client';
import type {
  AdjustmentCreateInput,
  AdjustmentProposalInput,
  InventoryAggregateDto,
  InventoryMovementLogRow,
  InventorySummaryRow,
  MovementsFilter,
} from './types';

export interface InventoryListMeta {
  total: number;
  page: number;
  per_page: number;
}

// ---- legacy aggregated /inventory ------------------------------------------

export const fetchInventory = async (
  batchId: string,
  params: { page?: number; per_page?: number } = {},
): Promise<{ data: InventoryAggregateDto[]; meta: InventoryListMeta }> => {
  const res = await apiClient.get<ApiSuccess<InventoryAggregateDto[], InventoryListMeta>>(
    `/monthly-batches/${batchId}/inventory`,
    { params },
  );
  const data = res.data.data ?? [];
  const meta = res.data.meta ?? {
    total: data.length,
    page: params.page ?? 1,
    per_page: params.per_page ?? data.length,
  };
  return { data, meta };
};

// ---- /inventory/summary -----------------------------------------------------

export const fetchInventorySummary = async (batchId: string): Promise<InventorySummaryRow[]> => {
  const res = await apiClient.get<ApiSuccess<InventorySummaryRow[]>>(
    `/monthly-batches/${batchId}/inventory/summary`,
  );
  return res.data.data ?? [];
};

// ---- /inventory/movements ---------------------------------------------------

export const fetchInventoryMovements = async (
  batchId: string,
  filter: MovementsFilter = {},
): Promise<InventoryMovementLogRow[]> => {
  const params: Record<string, string> = {};
  if (filter.material_id) params.material_id = filter.material_id;
  if (filter.type) params.type = filter.type;
  if (filter.from) params.from = filter.from;
  if (filter.to) params.to = filter.to;
  if (filter.source_type) params.source_type = filter.source_type;
  const res = await apiClient.get<ApiSuccess<InventoryMovementLogRow[]>>(
    `/monthly-batches/${batchId}/inventory/movements`,
    { params },
  );
  return res.data.data ?? [];
};

// ---- /inventory/adjustments (approval proposal) -----------------------------

export interface SubmitAdjustmentResponse {
  audit_approval_id: string;
  status: string;
}

export const submitInventoryAdjustment = async (
  batchId: string,
  body: AdjustmentProposalInput,
): Promise<SubmitAdjustmentResponse> => {
  const res = await apiClient.post<ApiSuccess<SubmitAdjustmentResponse>>(
    `/monthly-batches/${batchId}/inventory/adjustments`,
    body,
  );
  return res.data.data;
};

// ---- legacy /inventory-movements (approval proposal compatibility) ---------

export const createInventoryMovement = async (
  batchId: string,
  body: AdjustmentCreateInput,
): Promise<SubmitAdjustmentResponse> => {
  const res = await apiClient.post<ApiSuccess<SubmitAdjustmentResponse>>(
    `/monthly-batches/${batchId}/inventory-movements`,
    body,
  );
  return res.data.data;
};

export const takeInventorySnapshot = async (batchId: string): Promise<void> => {
  await apiClient.post(`/monthly-batches/${batchId}/inventory-snapshot`);
};

export const takeInventorySnapshotFromProduction = async (batchId: string): Promise<void> => {
  await apiClient.post(`/monthly-batches/${batchId}/inventory-snapshot:from-production`);
};
